import React from 'react';
import { Text, LogoSpotify } from '@spotify-internal/encore-web';
import { Benefits } from '../../types/referrals';
import { useImpressionTracking } from '../../lib/tracking/useImpressionTracking';
import { ICON_MAP } from './benefits-data';
import styles from './BenefitsSection.module.scss';

const noop = () => {};

type BenefitsSectionProps = {
  benefits: Benefits;
  onBenefitImpression?: (benefit: string, position: number) => void;
};

export function BenefitsSection({
  benefits,
  onBenefitImpression,
}: BenefitsSectionProps) {
  const observe = useImpressionTracking(
    onBenefitImpression
      ? (key: string) => {
          const index = benefits.items.findIndex(b => b.iconName === key);
          onBenefitImpression(key, index);
        }
      : noop,
  );

  return (
    <section className={styles.section}>
      <div className={styles.premiumBadge}>
        <LogoSpotify condensed height="16" semanticColor="textBase" label="" />
        <Text variant="bodyMediumBold" className={styles.premiumText}>
          Premium
        </Text>
      </div>
      <Text variant="titleLarge" as="h2" className={styles.heading}>
        {benefits.header}
      </Text>
      <ul className={styles.grid}>
        {benefits.items.map(({ iconName, title, subtitle }, index) => {
          const Icon = ICON_MAP[iconName];
          if (!Icon) return null;
          return (
            <li
              key={index}
              ref={onBenefitImpression ? observe(iconName) : undefined}
              className={styles.item}
            >
              <div className={styles.icon} aria-hidden="true">
                <Icon iconSize={24} className={styles.iconSmall} title="" />
                <Icon iconSize={48} className={styles.iconLarge} title="" />
              </div>
              <Text variant="titleSmall" className={styles.benefitTitle}>
                {title}
              </Text>
              <Text variant="bodySmall" className={styles.benefitSubtitle}>
                {subtitle}
              </Text>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
